// ─────────────────────────────────────────────────────────────────────────────
// Practice-streak client helpers.
//
// A streak day counts if the user completed an intervention OR listened
// to a library audio for 2+ minutes. Client responsibilities:
//   • Compute the user's *local* YYYY-MM-DD (not UTC) so late-night
//     practice doesn't get split by the timezone boundary.
//   • Fire `postStreakEvent` at qualifying moments (see call sites in
//     InterventionFlow + LibraryAudioContext).
//   • Dedupe library "2 min listened" events per-practice per-session so
//     scrubbing back and forth doesn't spam the endpoint.
//   • Fetch `getStreak()` on Dashboard mount for the header pill.
//   • Acknowledge celebrated milestones so we don't re-show the modal.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Local YYYY-MM-DD, honouring the browser/device timezone. */
export const todayLocalDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const getStreak = async () => {
  try {
    const res = await axios.get(`${API}/streak`);
    return res.data;
  } catch {
    return { current: 0, longest: 0, last_event_date: null, celebrated: [], next_milestone: 3 };
  }
};

/**
 * Record a streak-qualifying event. Returns the updated snapshot
 * (including `milestone_reached` — null unless a milestone was just
 * newly reached). Always resolves; errors are swallowed so a broken
 * streak call never blocks the completion flow.
 */
export const postStreakEvent = async ({ source, refId }) => {
  try {
    const res = await axios.post(`${API}/streak/event`, {
      source,
      ref_id: refId,
      local_date: todayLocalDate(),
    });
    return res.data;
  } catch {
    return null;
  }
};

export const markCelebrated = async (milestone) => {
  try {
    const res = await axios.post(`${API}/streak/celebrated/${milestone}`);
    return res.data;
  } catch {
    return null;
  }
};
